
import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { navigateToSubscription } from '../../analytics';
import { useAuth } from '../../context/AuthContext';
import { FeatureFlag, useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useSafeTwilioConversations } from '../../contexts/TwilioConversationsContext';
import { useIOSReaderMode } from '../../hooks/useIOSReaderMode';
import NotificationBell from '../NotificationBell/NotificationBell';
import MessagesPopover from '../MessagesPopover/MessagesPopover';
import { Capacitor } from '@capacitor/core';
import './DesktopHeader.css';
import { useChat } from '../../contexts/ChatContext';
import { Tooltip } from '@mui/material';
import { useDevNightMode } from '../../hooks/useDevNightMode';
import { DayNightThemeToggle } from '../DayNightThemeToggle/DayNightThemeToggle';
import { HELP_CENTER_URL } from '../../config/helpCenter';
import BugReportModal from '../BugReportModal';
import ProPublicPageHeaderLink from '../ProPublicPageHeaderLink';

import {
  ArrowDropDown,
  ArrowDropUp,
  BugReportOutlined,
  Campaign,
  CardGiftcard,
  Groups,
  HelpOutline,
  PersonOutline,
  Settings,
  WorkspacePremium,
} from '@mui/icons-material';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
const DesktopHeader: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const {
        isDevNightModeAllowed,
        devNightModeEnabled,
        setDevNightModeEnabled,
    } = useDevNightMode();
    const navigate = useNavigate();
    const location = useLocation();
    const { isIOSFreeUser } = useIOSReaderMode();
    const { isFeatureEnabled } = useFeatureFlags();
    const laikaUiEnabled = isFeatureEnabled(FeatureFlag.LAIKA);
    const chatFeatureEnabled = isFeatureEnabled(FeatureFlag.CHAT_FEATURE);
    const ambassadorEnabled = isFeatureEnabled(FeatureFlag.AMBASSADOR);
    const { openChat, isChatOpen, closeChat } = useChat();

    // Messages State
    // ...
    const { conversations, refreshUnreadMessagesCount } = useSafeTwilioConversations();
    const [unreadCount, setUnreadCount] = useState(0);

    // Profile Menu State
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [isMessagesPopoverOpen, setIsMessagesPopoverOpen] = useState(false);
    const [isBugModalOpen, setIsBugModalOpen] = useState(false);

    // Helper for onboarding status (simplified from Sidebar)
    const countUnreadMessages = useCallback(() => {
        if (!conversations || !Array.isArray(conversations)) return 0;
        let count = 0;
        for (const conv of conversations) {
            if ((conv.unreadMessagesCount && conv.unreadMessagesCount > 0) || conv.hasUnreadMessages) {
                count++;
            }
        }
        return count;
    }, [conversations]);

    useEffect(() => {
        setUnreadCount(countUnreadMessages());
    }, [conversations, countUnreadMessages]);

    const handleMessagesClick = () => {
        if (refreshUnreadMessagesCount) {
            refreshUnreadMessagesCount();
        }
        setIsMessagesPopoverOpen(!isMessagesPopoverOpen);
        setIsProfileMenuOpen(false);
    };

    const toggleProfileMenu = () => {
        setIsProfileMenuOpen(!isProfileMenuOpen);
    };

    // Close profile menu and messages popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (
                !target.closest('.desktop-profile-section') &&
                !target.closest('.desktop-header-link.messages-trigger') &&
                !target.closest('.messages-popover')
            ) {
                setIsProfileMenuOpen(false);
                setIsMessagesPopoverOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const defaultProfilePic = "https://rintintin-bucket.s3.eu-west-3.amazonaws.com/default-profile.jpg";
    const profilePic = user?.profile_photo_url || defaultProfilePic;
    const proName = user?.name || "Pro";

    // Check onboarding status for red dot
    // Simplified logic: create a custom hook or just check basic props if needed.
    // Sidebar logic was complex, using local storage. For now, rely on `user.profile_complete`.
    const onboardingComplete = !!user?.profile_complete;

    const goToPremium = () => {
        navigateToSubscription(navigate, 'header');
        setIsProfileMenuOpen(false);
    };

    const isProfileRouteActive = (path: string) => {
        if (path === '/app/settings') {
            return location.pathname.startsWith('/app/settings');
        }
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    return (
        <>
        <div className="desktop-header-content">
            <div className="header-island">
                <ProPublicPageHeaderLink variant="desktop" />

                {/* Laika */}
                {!isIOSFreeUser && laikaUiEnabled && (
                    <div
                        className={`desktop-header-link laika-trigger ${isChatOpen ? 'active' : ''
                            }`}
                        onClick={isChatOpen ? closeChat : () => openChat('header')}
                        title={t('chatWidget.openAriaLabel', { ns: 'messaging' })}
                        style={{ marginRight: '4px' }}
                    >
                        <div className="icon-wrapper laika-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.918a2 2 0 0 0 1.437-1.437l1.582-6.135a.5.5 0 0 1 .963 0l1.582 6.135a2 2 0 0 0 1.437 1.437l6.135 1.582a.5.5 0 0 1 0 .963L14.063 14.5a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0l-1.582-6.135Z" />
                                <path d="M20 3v4" />
                                <path d="M22 5h-4" />
                                <path d="M4 17v2" />
                                <path d="M5 18H3" />
                            </svg>
                        </div>
                    </div>
                )}

                {/* Messages Link */}
                {!isIOSFreeUser && chatFeatureEnabled && (
                    <div className="messages-relative-container">
                        <div
                            className={`desktop-header-link messages-trigger ${location.pathname.includes('/messages') || isMessagesPopoverOpen ? 'active' : ''}`}
                            onClick={handleMessagesClick}
                            role="button"
                            aria-label={t('common:messages')}
                        >
                            <div className="icon-wrapper messages-icon-wrapper">
                                <SmsOutlinedIcon className="header-messages-icon" />
                                {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
                            </div>
                        </div>
                        {isMessagesPopoverOpen && (
                            <MessagesPopover
                                onClose={() => setIsMessagesPopoverOpen(false)}
                            />
                        )}
                    </div>
                )}

                {/* Notification Bell */}
                <div className="desktop-header-item">
                    <NotificationBell />
                </div>

                {isDevNightModeAllowed && (
                    <Tooltip title="Mode nuit" placement="bottom">
                        <div className="desktop-header-dev-night-toggle">
                            <DayNightThemeToggle
                                checked={devNightModeEnabled}
                                onChange={setDevNightModeEnabled}
                                aria-label="Basculer le mode nuit"
                            />
                        </div>
                    </Tooltip>
                )}

                {/* Profile Section */}
                <div className="desktop-profile-section">
                    <div className="desktop-profile-trigger" onClick={toggleProfileMenu}>
                        <img src={profilePic} alt="Profil" className="profile-pic-small" />
                        <span className="desktop-profile-name">{proName}</span>
                        {isProfileMenuOpen ? <ArrowDropUp /> : <ArrowDropDown />}
                    </div>

                    {/* Dropdown Menu */}
                    {isProfileMenuOpen && (
                        <div className="desktop-profile-dropdown">
                            <div className="dropdown-arrow"></div>

                            <div
                                className={`dropdown-item${isProfileRouteActive('/app/profil-v2') ? ' active' : ''}`}
                                onClick={() => { navigate('/app/profil-v2'); setIsProfileMenuOpen(false); }}
                            >
                                <PersonOutline />
                                <span>{t('sidebar:menu.profile')}</span>
                            </div>

                            {!isIOSFreeUser && (
                                <div
                                    className={`dropdown-item${isProfileRouteActive('/app/associations') ? ' active' : ''}`}
                                    onClick={() => { navigate('/app/associations'); setIsProfileMenuOpen(false); }}
                                >
                                    <Groups />
                                    <span>{t('sidebar:menu.association')}</span>
                                </div>
                            )}

                            {!isIOSFreeUser && (
                                <div
                                    className={`dropdown-item${isProfileRouteActive('/app/equipe') ? ' active' : ''}`}
                                    onClick={() => { navigate('/app/equipe'); setIsProfileMenuOpen(false); }}
                                >
                                    <Groups />
                                    <span>{t('sidebar:menu.team')}</span>
                                </div>
                            )}

                            <div
                                className={`dropdown-item${isProfileRouteActive('/app/settings') ? ' active' : ''}`}
                                onClick={() => { navigate('/app/settings'); setIsProfileMenuOpen(false); }}
                            >
                                <Settings />
                                <span>{t('sidebar:menu.settings')}</span>
                            </div>

                            {ambassadorEnabled && (
                                <div
                                    className={`dropdown-item${isProfileRouteActive('/app/ambassadeur') ? ' active' : ''}`}
                                    onClick={() => { navigate('/app/ambassadeur'); setIsProfileMenuOpen(false); }}
                                >
                                    <Campaign />
                                    <span>{t('sidebar:menu.ambassador')}</span>
                                </div>
                            )}

                            <div
                                className={`dropdown-item${isProfileRouteActive('/app/partenaires') ? ' active' : ''}`}
                                onClick={() => { navigate('/app/partenaires'); setIsProfileMenuOpen(false); }}
                            >
                                <CardGiftcard />
                                <span>{t('sidebar:menu.ourPartners')}</span>
                            </div>

                            <a
                                href={HELP_CENTER_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="dropdown-item"
                                onClick={() => setIsProfileMenuOpen(false)}
                            >
                                <HelpOutline />
                                <span>{t('sidebar:menu.helpCenter')}</span>
                            </a>

                            <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => {
                                    setIsProfileMenuOpen(false);
                                    setIsBugModalOpen(true);
                                }}
                            >
                                <BugReportOutlined />
                                <span>{t('sidebar:menu.reportBug')}</span>
                            </button>

                            {/* Premium Button */}
                            {user && !user.is_premium && !(Capacitor.getPlatform() === 'ios' && Capacitor.isNativePlatform()) && (
                                <div className="premium-item" onClick={goToPremium}>
                                    <WorkspacePremium />
                                    <span>{t('sidebar:menu.becomePremium')}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
        <BugReportModal isOpen={isBugModalOpen} onClose={() => setIsBugModalOpen(false)} />
        </>
    );
};

export default DesktopHeader;
